import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  ReferenceLine,
} from 'recharts';
import { OI_CHART, oiAxisTick, oiChartMargin, oiTooltipStyle } from './OiChartTheme';

export function OiAreaTrendChart({
  data,
  dataKey,
  xKey = 'label',
  height = 240,
  target,
}: {
  data: Array<Record<string, unknown>>;
  dataKey: string;
  xKey?: string;
  height?: number;
  target?: number;
}) {
  if (!data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={oiChartMargin}>
        <defs>
          <linearGradient id="oiAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={OI_CHART.primary} stopOpacity={0.3} />
            <stop offset="100%" stopColor={OI_CHART.primary} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={OI_CHART.grid} strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={oiAxisTick()} />
        <YAxis tick={oiAxisTick()} />
        <Tooltip contentStyle={oiTooltipStyle()} />
        {target != null ? <ReferenceLine y={target} stroke={OI_CHART.target} strokeDasharray="4 4" /> : null}
        <Area type="monotone" dataKey={dataKey} stroke={OI_CHART.primary} fill="url(#oiAreaFill)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function OiHorizontalBarChart({
  data,
  dataKey,
  nameKey,
  height = 240,
  colors,
}: {
  data: Array<Record<string, unknown>>;
  dataKey: string;
  nameKey: string;
  height?: number;
  colors?: string[];
}) {
  if (!data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ ...oiChartMargin, left: 80 }}>
        <CartesianGrid stroke={OI_CHART.grid} strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={oiAxisTick()} />
        <YAxis type="category" dataKey={nameKey} tick={oiAxisTick()} width={72} />
        <Tooltip contentStyle={oiTooltipStyle()} />
        <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={colors?.[i] ?? OI_CHART.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function OiDonutChart({
  data,
  height = 200,
}: {
  data: Array<{ name: string; value: number }>;
  height?: number;
}) {
  const filtered = data.filter((d) => d.value > 0);
  if (!filtered.length) return null;
  const palette = [OI_CHART.primary, OI_CHART.comparison, OI_CHART.positive, OI_CHART.target, OI_CHART.negative];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={filtered} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
          {filtered.map((_, i) => (
            <Cell key={i} fill={palette[i % palette.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={oiTooltipStyle()} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function OiRadialGauge({ score, label }: { score: number; label?: string }) {
  const tier =
    score > 95 ? OI_CHART.positive : score >= 85 ? OI_CHART.positive : score >= 70 ? OI_CHART.target : OI_CHART.negative;
  const data = [{ name: 'score', value: score }, { name: 'rest', value: 100 - score }];
  return (
    <div style={{ textAlign: 'center' }}>
      <ResponsiveContainer width="100%" height={120}>
        <PieChart>
          <Pie data={data} dataKey="value" startAngle={180} endAngle={0} innerRadius={40} outerRadius={56} cx="50%" cy="100%">
            <Cell fill={tier} />
            <Cell fill={OI_CHART.neutral} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="oi-mono-value" style={{ fontSize: 28, fontWeight: 700, marginTop: -24 }}>
        {score}%
      </div>
      {label ? <div style={{ fontSize: 11, color: 'var(--oi-text-muted)' }}>{label}</div> : null}
    </div>
  );
}

export function OiWaterfallChart({
  steps,
  height = 200,
}: {
  steps: Array<{ label: string; value: number }>;
  height?: number;
}) {
  if (!steps.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={steps} margin={oiChartMargin}>
        <CartesianGrid stroke={OI_CHART.grid} strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={oiAxisTick()} />
        <YAxis tick={oiAxisTick()} />
        <Tooltip contentStyle={oiTooltipStyle()} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {steps.map((s, i) => (
            <Cell key={i} fill={s.value < 0 ? OI_CHART.negative : OI_CHART.positive} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function OiStackedHorizontalBar({
  segments,
  height = 32,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  height?: number;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return null;
  const data = [{ ...Object.fromEntries(segments.map((s) => [s.label, s.value])) }];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" stackOffset="expand">
        {segments.map((seg) => (
          <Bar key={seg.label} dataKey={seg.label} stackId="a" fill={seg.color} radius={[2, 2, 2, 2]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function OiRiskHeatmap({ cells }: { cells: Array<{ x: number; y: number; count: number }> }) {
  const max = Math.max(1, ...cells.map((c) => c.count));
  return (
    <div className="oi-risk-matrix">
      {Array.from({ length: 25 }, (_, i) => {
        const x = (i % 5) + 1;
        const y = Math.floor(i / 5) + 1;
        const cell = cells.find((c) => c.x === x && c.y === y);
        const intensity = cell ? cell.count / max : 0;
        const bg = intensity > 0.7 ? OI_CHART.negative : intensity > 0.4 ? OI_CHART.target : intensity > 0 ? OI_CHART.primary : OI_CHART.neutral;
        return (
          <div key={i} className="oi-risk-matrix__cell" style={{ background: bg, opacity: cell ? 1 : 0.35 }}>
            {cell?.count ?? ''}
          </div>
        );
      })}
    </div>
  );
}
